import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { DialogTitle, DialogContent, DialogActions } from '@mui/material'

import {
  Container,
  CardBox,
  Checkbox,
  Dialog,
  NotificationDialog,
  Stack,
  Text,
  Typography,
  Button,
  useAlert,
  Link,
  Box,
} from '../../../components'
import { mutations, queries } from '../../../graphql'

const sections = [
  {
    id: 'estate',
    title: 'Estate Gifts (Bequests, RRSPs, RRIFs, TFSAs)',
    description:
      'Include GiveWise in your Will or name your Giving Fund as a beneficiary on registered accounts. A lasting legacy—simple to arrange and deeply impactful.',
    href: 'https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/estate-gifts',
  },
  {
    id: 'crypto',
    title: 'Cryptocurrency',
    description:
      'Donate Bitcoin or Ethereum directly. We handle the conversion and issue your tax receipt based on fair market value at the time of donation.',
    href: 'https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/cryptocurrency',
  },
  {
    id: 'flowThrough',
    title: 'Flow-Through Shares',
    description:
      'Unlock compounded tax benefits with donations of flow-through shares. GiveWise partners with firms like PearTree to streamline this specialized process.',
    href: 'https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/flow-through-mining-shares',
  },
  {
    id: 'insurance',
    title: 'Life Insurance',
    description:
      'Name your Giving Fund as a beneficiary or donate a policy. This allows for a larger future gift with smaller premiums today.',
    href: 'https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/life-insurance',
  },
  {
    id: 'preferredShares',
    title: 'Preferred Shares of Private Corporations',
    description:
      'Donate shares from a business you own. We’ll guide you through a CRA-compliant process, helping you reduce taxes on a future sale or succession plan.',
    href: 'https://guide.givewise.ca/for-donors-getting-started-with-givewise/making-a-contribution/contribution-payment-methods/preferred-shares-of-a-private-corp.',
  },
]

export function OtherAssets() {
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const { data: { me } = {}, loading: profileLoading } = useQuery(queries.user.me)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [emailError, setEmailError] = useState(null)

  const [requestGivingOptionsInfo, { loading }] = useMutation(mutations.user.requestGivingOptionsInfo, {
    onCompleted: () => {
      setAlert({
        severity: 'success',
        message: 'Request sent! Our team will reach out shortly.',
      })
    },
    onError: (e) => {
      setAlert({
        severity: 'error',
        message: `Failed to send request: ${e.message}`,
      })
    },
  })

  const [selectedOptions, setSelectedOptions] = useState([])

  const handleOptionChange = (id) => {
    setSelectedOptions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const handleRequestInfo = () => {
    if (selectedOptions.length === 0) {
      setAlert({
        severity: 'error',
        message: 'Please select at least one asset before submitting.',
      })
      return
    }
    setEmailError(null)
    requestGivingOptionsInfo({
      variables: {
        email: me?.email,
        fundId: me?.fund?.id,
        options: selectedOptions,
        isDonatingToFund: false,
        donationTargetName: 'Contribution',
        donationTargetId: 'Contribution',
      },
    })
      .then(() => {
        setDialogOpen(true)
      })
      .catch((e) => {
        setEmailError(e.message)
        setDialogOpen(true)
      })
  }

  return (
    <>
      <Container maxWidth="lg">
        <CardBox title="Give Using Other Assets">
          <Stack>
            <Typography>
              These are powerful ways to give beyond cash, often with unique tax advantages. We’ll work with you to
              structure your gift efficiently.
            </Typography>
            <Typography sx={{ mb: 2, fontWeight: 600 }}>
              Select the types of gifts you’re interested in. We’ll be in touch with more information and to help you
              explore what’s possible:
            </Typography>

            {sections.map(({ id, title, description, href }) => (
              <Stack key={id}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={selectedOptions.includes(id)}
                    onChange={() => handleOptionChange(id)}
                    inputProps={{ 'aria-label': `Select ${title}` }}
                  />
                  <Typography variant="h6">{title}</Typography>
                </Stack>

                <Box sx={{ pl: 6 }}>
                  <Typography>
                    {description}{' '}
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      Learn more.
                    </a>
                  </Typography>
                </Box>
              </Stack>
            ))}

            <Box sx={{ pt: 4 }}>
              <Button
                variant="contained"
                onClick={handleRequestInfo}
                disabled={loading}
                sx={{ alignSelf: 'flex-start' }}
              >
                Request More Info
              </Button>
            </Box>

            <Alert {...alertProps} />
          </Stack>
        </CardBox>
      </Container>
      {emailError ? (
        <NotificationDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          type="error"
          title="Error"
          message={
            <Text.Body>
              There was an issue sending your request: {emailError}
              Please try again or contact support.
            </Text.Body>
          }
          actions={[
            {
              label: 'Close',
              onClick: () => setDialogOpen(false),
            },
          ]}
        />
      ) : (
        <NotificationDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          type="success"
          title="Thank you for your interest!"
          message={
            <>
              <Text.Body sx={{ mb: 3 }}>
                <strong>Thank you for expressing interest in planning a future gift!</strong>
              </Text.Body>
              <Text.Body sx={{ mb: 3 }}>
                Our team will connect with you shortly to discuss donating:
                <ul>
                  {selectedOptions.map((optionId) => {
                    const option = sections.find((opt) => opt.id === optionId)
                    return option ? <li key={optionId}>{option.title}</li> : null
                  })}
                </ul>
              </Text.Body>
              <Text.Body sx={{ mb: 3 }}>
                Keep an eye on your inbox — and if you don&apos;t hear from us soon, be sure to check your spam or junk
                folder just in case.
              </Text.Body>
            </>
          }
          actions={[
            {
              label: 'Done',
              onClick: () => setDialogOpen(false),
            },
          ]}
        />
      )}
    </>
  )
}
